import { mat4, vec3,vec4 } from "wgpu-matrix";
import Camera from "./camera";
import { Ray3 } from "../collision/ray_types";

export default class DioramaCamera extends Camera {
  target: Float32Array;

  yaw: number;
  pitch: number;
  distance: number;

  min_distance: number;
  max_distance: number;

  pan_speed: number;
  zoom_speed: number;

  bounds_min_x: number;
  bounds_max_x: number;
  bounds_min_z: number;
  bounds_max_z: number;

  constructor() {
    super();

    this.target = vec3.fromValues(0, 1.6, 0);

    this.yaw = Math.PI * 0.12;
    this.pitch = 0.30;
    this.distance = 18.0;

    this.min_distance = 8.0;
    this.max_distance = 40.0;

    this.pan_speed = 0.01;
    this.zoom_speed = 0.0015;

    this.bounds_min_x = -1000;
    this.bounds_max_x = 1000;
    this.bounds_min_z = -1000;
    this.bounds_max_z = 1000;

    this.fov_y = Math.PI / 7.0;
    this.far = 300.0;

    this.update_matrices();
  }

  set_fov_y(fov:number)
  {
    this.fov_y = fov;
  }
  set_distance_bounds(min_distance: number, max_distance: number): void
  {
    this.min_distance = min_distance;
    this.max_distance = max_distance;
    this.set_distance(this.distance);
}

  set_target(x: number, y: number, z: number) {
    this.target[0] = x;
    this.target[1] = y;
    this.target[2] = z;
    this.clamp_target();
    this.update_matrices();
  }

  set_style_angle(yaw: number, pitch: number) {
    this.yaw = yaw;
    this.pitch = pitch;
    this.update_matrices();
  }

  set_distance(distance: number) {
    this.distance = Math.max(this.min_distance, Math.min(this.max_distance, distance));
    this.update_matrices();
  }

  set_pan_bounds(min_x: number, max_x: number, min_z: number, max_z: number) {
    this.bounds_min_x = min_x;
    this.bounds_max_x = max_x;
    this.bounds_min_z = min_z;
    this.bounds_max_z = max_z;
    this.clamp_target();
    this.update_matrices();
  }

  pan(delta_x: number, delta_z: number) {
    this.target[0] += delta_x * this.pan_speed;
    this.target[2] += delta_z * this.pan_speed;
    this.clamp_target();
    this.update_matrices();
  }

  zoom(delta: number) {
    this.distance += delta * this.zoom_speed;
    if (this.distance < this.min_distance) this.distance = this.min_distance;
    if (this.distance > this.max_distance) this.distance = this.max_distance;
    this.update_matrices();
  }

  follow_xz(x: number, z: number, smoothing: number = 0.1) {
    this.target[0] += (x - this.target[0]) * smoothing;
    this.target[2] += (z - this.target[2]) * smoothing;
    this.clamp_target();
    this.update_matrices();
  }

  get_forward_xz(): Float32Array {
    const dx = this.target[0] - this.eye[0];
    const dz = this.target[2] - this.eye[2];
    const len = Math.hypot(dx, dz) || 1.0;
    return new Float32Array([dx / len, dz / len]);
  }

  get_shared_panel_yaw(): number {
    return this.yaw;
  }

   get_screen_ray(
  screen_x: number,
  screen_y: number,
  viewport_width: number,
  viewport_height: number
): Ray3 {
  const ndc_x = (screen_x / viewport_width) * 2.0 - 1.0;
  const ndc_y = 1.0 - (screen_y / viewport_height) * 2.0;

  const inv_view_proj = mat4.inverse(this.view_projection_matrix);

  const near4 = vec4.transformMat4(
    vec4.create(ndc_x, ndc_y, 0.0, 1.0),
    inv_view_proj
  );

  const far4 = vec4.transformMat4(
    vec4.create(ndc_x, ndc_y, 1.0, 1.0),
    inv_view_proj
  );

  const near_w = Math.abs(near4[3]) > 0.000001 ? near4[3] : 1.0;
  const far_w = Math.abs(far4[3]) > 0.000001 ? far4[3] : 1.0;

  const near_point = new Float32Array([
    near4[0] / near_w,
    near4[1] / near_w,
    near4[2] / near_w,
  ]);

  const far_point = new Float32Array([
    far4[0] / far_w,
    far4[1] / far_w,
    far4[2] / far_w,
  ]);

  const dx = far_point[0] - near_point[0];
  const dy = far_point[1] - near_point[1];
  const dz = far_point[2] - near_point[2];
  const len = Math.hypot(dx, dy, dz) || 1.0;

  return {
    origin: near_point,
    direction: new Float32Array([
      dx / len,
      dy / len,
      dz / len,
    ]),
  };
}


  private clamp_target() {
    if (this.target[0] < this.bounds_min_x) this.target[0] = this.bounds_min_x;
    if (this.target[0] > this.bounds_max_x) this.target[0] = this.bounds_max_x;
    if (this.target[2] < this.bounds_min_z) this.target[2] = this.bounds_min_z;
    if (this.target[2] > this.bounds_max_z) this.target[2] = this.bounds_max_z;
  }

  update_matrices() {
    const cos_pitch = Math.cos(this.pitch);
    const sin_pitch = Math.sin(this.pitch);
    const cos_yaw = Math.cos(this.yaw);
    const sin_yaw = Math.sin(this.yaw);

    this.eye[0] = this.target[0] + this.distance * cos_pitch * sin_yaw;
    this.eye[1] = this.target[1] + this.distance * sin_pitch;
    this.eye[2] = this.target[2] + this.distance * cos_pitch * cos_yaw;

    this.update_projection_matrix();

    this.view_matrix = mat4.lookAt(
      this.eye,
      this.target,
      this.up
    );

    this.update_view_projection_matrix();
  }
}